import { useState } from 'react';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from '@/components/ui/sheet';
import type { MemoryRecord } from './MemoryRecordsBrowser';

interface MemoryRecordDetailProps {
    record: MemoryRecord | null;
    onClose: () => void;
    onStatusUpdated?: () => void;
}

const statusColors: Record<string, string> = {
    proposed: 'bg-amber-100 text-amber-700',
    confirmed: 'bg-green-100 text-green-700',
    stale: 'bg-slate-100 text-slate-500',
    superseded: 'bg-red-100 text-red-600',
    disputed: 'bg-orange-100 text-orange-700',
};

const scopeColors: Record<string, string> = {
    session: 'bg-purple-100 text-purple-700',
    workflow: 'bg-blue-100 text-blue-700',
    user: 'bg-indigo-100 text-indigo-700',
    app: 'bg-teal-100 text-teal-700',
    tenant: 'bg-cyan-100 text-cyan-700',
    global: 'bg-slate-100 text-slate-600',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="space-y-1">
            <dt className="text-[10px] font-medium uppercase tracking-wide text-slate-400">{label}</dt>
            <dd className="text-sm text-slate-900">{children}</dd>
        </div>
    );
}

function formatTimestamp(iso?: string): string {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    } catch {
        return iso;
    }
}

export function MemoryRecordDetail({ record, onClose, onStatusUpdated }: MemoryRecordDetailProps) {
    const [updating, setUpdating] = useState(false);

    const updateStatus = async (newStatus: string) => {
        if (!record) return;
        setUpdating(true);
        try {
            await api.patch(`/memory-records/${record.id}`, { status: newStatus });
            onStatusUpdated?.();
            onClose();
        } catch {
            // error handled by api client interceptor
        } finally {
            setUpdating(false);
        }
    };

    return (
        <Sheet open={!!record} onOpenChange={open => { if (!open) onClose(); }}>
            <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
                <SheetHeader>
                    <SheetTitle className="text-base">Memory Record</SheetTitle>
                    <SheetDescription className="text-xs">
                        {record?.id}
                    </SheetDescription>
                </SheetHeader>

                {record && (
                    <div className="px-4 pb-6 space-y-5">
                        {/* Status + Scope badges */}
                        <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[record.status] ?? 'bg-slate-100 text-slate-600'}`}>
                                {record.status}
                            </span>
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${scopeColors[record.scope] ?? 'bg-slate-100 text-slate-600'}`}>
                                {record.scope}
                            </span>
                        </div>

                        {/* Subject-Predicate-Object triple */}
                        <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 space-y-3">
                            <Field label="Subject">
                                <span className="text-slate-400 text-xs">{record.subject_type}/</span>
                                <span className="font-medium">{record.subject_id}</span>
                            </Field>
                            <Field label="Predicate">
                                <span className="font-mono text-xs">{record.predicate}</span>
                            </Field>
                            <Field label="Object">
                                <pre className="text-xs bg-white border border-slate-200 rounded-md p-3 overflow-x-auto max-h-48 whitespace-pre-wrap">
                                    {JSON.stringify(record.object, null, 2)}
                                </pre>
                            </Field>
                        </div>

                        {/* Metadata */}
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Category">{String(record.category ?? '—')}</Field>
                            <Field label="Confidence">
                                {record.confidence != null ? `${Math.round(record.confidence * 100)}%` : '—'}
                            </Field>
                            {record.capability_ref && (
                                <Field label="Capability Ref">{record.capability_ref}</Field>
                            )}
                            {record.app_key && (
                                <Field label="App Key">{record.app_key}</Field>
                            )}
                            {record.user_id && (
                                <Field label="User ID">{record.user_id}</Field>
                            )}
                            {record.dedupe_key && (
                                <Field label="Dedupe Key">
                                    <span className="font-mono text-[10px] break-all">{record.dedupe_key}</span>
                                </Field>
                            )}
                        </div>

                        {/* Tags */}
                        {record.tags && record.tags.length > 0 && (
                            <Field label="Tags">
                                <div className="flex flex-wrap gap-1">
                                    {record.tags.map(tag => (
                                        <span key={tag} className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </Field>
                        )}

                        {/* Source / Provenance */}
                        {record.source !== undefined && record.source !== null ? (
                            <Field label="Source / Provenance">
                                <pre className="text-xs bg-white border border-slate-200 rounded-md p-3 overflow-x-auto max-h-32 whitespace-pre-wrap">
                                    {JSON.stringify(record.source, null, 2)}
                                </pre>
                            </Field>
                        ) : null}

                        {/* Timestamps */}
                        <div className="grid grid-cols-2 gap-4 border-t border-slate-100 pt-4">
                            <Field label="Created">{formatTimestamp(record.created)}</Field>
                            <Field label="Updated">{formatTimestamp(record.modified)}</Field>
                            {record.effective_from && (
                                <Field label="Effective From">{formatTimestamp(record.effective_from)}</Field>
                            )}
                            {record.effective_to && (
                                <Field label="Effective To">{formatTimestamp(record.effective_to)}</Field>
                            )}
                            {record.ttl_at && (
                                <Field label="TTL Expires">{formatTimestamp(record.ttl_at)}</Field>
                            )}
                            {record.created_by && (
                                <Field label="Created By">{record.created_by}</Field>
                            )}
                            {record.updated_by && (
                                <Field label="Updated By">{record.updated_by}</Field>
                            )}
                        </div>

                        {/* Actions */}
                        {record.status !== 'superseded' && (
                            <div className="flex items-center gap-2 border-t border-slate-100 pt-4">
                                {record.status !== 'confirmed' && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="gap-1.5 text-green-700 border-green-200 hover:bg-green-50"
                                        disabled={updating}
                                        onClick={() => updateStatus('confirmed')}
                                    >
                                        {updating ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                                        Confirm
                                    </Button>
                                )}
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
                                    disabled={updating}
                                    onClick={() => updateStatus('superseded')}
                                >
                                    {updating ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                                    Supersede
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
